import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Combobox, Transition } from '@headlessui/react';
import cn from 'classnames';

import { CaretDown } from '@phosphor-icons/react';

function Select({
  data,
  name,
  value,
  readOnly,
  onChange,
  inputClassName,
  containerClassName,
  optionsItemClassName,
  optionsContainerClassName,
}) {
  const [selected, setSelected] = useState(value);
  const [query, setQuery] = useState('');

  const filteredData =
    query === ''
      ? data
      : data.filter((item) =>
          item.value
            .toLowerCase()
            .includes(query.toLowerCase().replace(/\s+/g, '')),
        );

  const handleClickClear = () => setSelected(data?.[0]);

  useEffect(() => {
    onChange((prev) => ({
      ...prev,
      [name]: selected,
    }));
  }, [selected, name, onChange]);

  return (
    <Combobox value={selected} onChange={setSelected}>
      <div className={cn('w-[260px] relative', containerClassName)}>
        <div className="relative w-full text-left bg-white rounded border border-neutral-300 cursor-default focus-within:border-brand-600">
          <Combobox.Input
            className={cn(
              'h-[34px] w-full bg-white border-none text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:rounded pl-3 pr-9',
              inputClassName,
            )}
            displayValue={(item) => item.label}
            onChange={(e) => setQuery(e.target.value)}
            readOnly={readOnly}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <CaretDown
              size={14}
              className="text-neutral-500"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options
            className={cn(
              'absolute z-20 w-full top-10 left-0 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-popover',
              optionsContainerClassName,
            )}
          >
            {filteredData.length === 0 && query !== '' ? (
              <div className="cursor-default bg-white select-none relative py-2 px-4 text-sm text-neutral-500 text-center">
                Nothing found.
              </div>
            ) : (
              filteredData.map((item) => (
                <Combobox.Option
                  key={item.value}
                  value={item}
                  className={({ active }) =>
                    `cursor-pointer text-sm select-none relative py-2 px-3 text-neutral-700 ${optionsItemClassName} ${
                      active ? 'bg-neutral-100 text-neutral-900' : 'bg-white'
                    }`
                  }
                >
                  {item.label}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>

        {selected.value !== data?.[0].value && (
          <div className="absolute -right-20 top-1 hidden">
            <button
              type="button"
              className="text-danger-700 text-xs font-semibold"
              onClick={handleClickClear}
            >
              CLEAR ALL
            </button>
          </div>
        )}
      </div>
    </Combobox>
  );
}

Select.defaultProps = {
  readOnly: true,
  inputClassName: '',
  containerClassName: '',
  optionsItemClassName: '',
  optionsContainerClassName: '',
};

Select.propTypes = {
  readOnly: PropTypes.bool,
  inputClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  optionsItemClassName: PropTypes.string,
  optionsContainerClassName: PropTypes.string,

  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.instanceOf(Object).isRequired,
};

export default Select;
